import * as React from "react"
import { MdArrowForward } from "react-icons/md"
import { FormattedMessage } from "@modules/locales"
import { Button, InputConnectedField, Text } from "gatsby-interface"
import { Form, FormElement } from "@modules/form/components/Form"
import {
  cmsIntegration as cmsIntegrationText,
  ui as uiText,
} from "@modules/locales/default.js"
import { CmsVendor } from "@modules/graphql/types"
import { useInstallShopifyCmsIntegrationMutation } from "@modules/cms/shared/queries.generated"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useTracker } from "@modules/analytics"

type FormValues = {
  password: string
}

export type ShopifyAuthFormProps = {
  siteId: string
  isProvisioning?: boolean
  onComplete: () => void
  onCancel: () => void
}

export function ShopifyAuthForm({
  siteId,
  isProvisioning,
  onComplete,
  onCancel,
}: ShopifyAuthFormProps) {
  const [authenticateShopify] = useInstallShopifyCmsIntegrationMutation()
  const [setError, errorAlert] = useTriggerErrorAlert()
  const { trackButtonClicked } = useTracker()

  return (
    <Form<FormValues>
      initialValues={{ password: `` }}
      onSubmit={values =>
        authenticateShopify({
          variables: {
            password: values.password,
            siteId,
            provision: isProvisioning,
          },
          refetchQueries: [
            `cmsIntegrationsForSite`,
            `cmsIntegrationForProvisioning`,
          ],
        })
          .then(({ data }) => {
            trackButtonClicked(`Authorize with ${CmsVendor.Shopify}`, {
              siteId: siteId,
              uiSource:
                origin === `settings`
                  ? `Site settings: integrations`
                  : `New Site Wizard: integrations`,
            })

            if (data?.installShopifyCmsIntegration?.success) {
              return onComplete()
            }

            const errMesage = data?.installShopifyCmsIntegration?.message

            if (errMesage) {
              setError({ message: errMesage })
            }
          })
          .catch(err => setError({ message: err.message }))
      }
    >
      {() => (
        <FormElement noValidate applySpacing>
          <React.Fragment>
            <Text size="S" css={{ margin: 0 }}>
              <FormattedMessage
                message={cmsIntegrationText.messages.shopifyFindPrivateAppCreds}
              />
            </Text>
            <InputConnectedField
              label={cmsIntegrationText.labels.shopifyPassword}
              name="password"
              type="password"
              required
            />
          </React.Fragment>
          {errorAlert}
          <div
            css={{
              display: `flex`,
              alignItems: `center`,
              justifyContent: `space-between`,
            }}
          >
            <Button onClick={onCancel} tone="NEUTRAL" variant="SECONDARY">
              {uiText.actions.cancel}
            </Button>
            <Button
              type="submit"
              tone="BRAND"
              variant="PRIMARY"
              rightIcon={<MdArrowForward />}
            >
              {cmsIntegrationText.actions.authorize}
            </Button>
          </div>
        </FormElement>
      )}
    </Form>
  )
}
